import { inject } from '@angular/core';
import { CanActivateChildFn, CanActivateFn, Router, Routes } from '@angular/router';

import { AuthService } from './core/services/auth.service';
import { ActingContextService } from './core/services/acting-context.service';

const authRequiredGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);

  if (authService.isAuthenticated()) {
    return true;
  }

  await authService.login();
  return false;
};

const emailVerificationGate: CanActivateChildFn = (_route, state) => {
  const authService = inject(AuthService);
  const path = state.url.split(/[?#]/, 1)[0];

  if (
    !authService.isAuthenticated() ||
    !authService.emailVerificationRequired() ||
    path === '/email-verification'
  ) {
    return true;
  }

  authService.saveEmailVerificationReturnPath(state.url);
  return inject(Router).createUrlTree(['/email-verification']);
};

const emailVerificationPageGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);

  if (!authService.isAuthenticated()) {
    await authService.login();
    return false;
  }

  // This route also supports adding/changing the email later from the account menu.
  return true;
};

const userManagementGuard: CanActivateFn = () => {
  const authService = inject(AuthService);

  if (authService.hasRole('ADMIN') || authService.hasRole('PROVINCIAL_ADMIN')) {
    return true;
  }

  return inject(Router).createUrlTree(['/home']);
};

/**
 * ยื่นคำขอใหม่ on a citizen's behalf is a right of its own (issue #412), separate from being
 * staff. A citizen files for themself and passes; a staff user without the right is sent to a
 * page that says so, never into the wizard in citizen mode. Permit refuses such a filing anyway.
 *
 * <p>It sits on the filing routes one by one, never on a `license-application*` prefix: staff
 * witnesses sign on `…/sign/:id`, and the payment and status-tracking pages are follow-up of a
 * request already filed, which the right does not gate.
 */
export const fileOnBehalfGuard: CanActivateFn = () => {
  const authService = inject(AuthService);

  if (!authService.isStaff() || authService.canFileOnBehalf()) {
    return true;
  }

  return inject(Router).createUrlTree(['/staff-filing-denied']);
};

const citizenOnlyGuard: CanActivateFn = () => {
  const authService = inject(AuthService);

  if (authService.canManageCompanies()) {
    return true;
  }

  return inject(Router).createUrlTree(['/home']);
};

/**
 * A company member may open the signing menu only when Keycloak put the company's signing group
 * into this session. The permit backend re-checks the company row on every read/write; this guard
 * only keeps a stale/deep-linked company page out of the portal navigation.
 */
const signingAccessGuard: CanActivateFn = () => {
  const authService = inject(AuthService);
  const actingContext = inject(ActingContextService);

  if (!actingContext.isCompany() || authService.isAdmin()) {
    return true;
  }

  return authService.organizationGroups().includes('permission-signing')
    ? true
    : inject(Router).createUrlTree(['/home']);
};

/**
 * Status tracking is shared by the representative and effective company members only. The
 * backend enforces the same rule, while this guard prevents a revoked/suspended member from
 * opening the page directly by URL after the menu has disappeared.
 */
const trackingAccessGuard: CanActivateFn = async () => {
  const authService = inject(AuthService);
  if (!authService.isAuthenticated()) {
    await authService.login();
    return false;
  }

  const actingContext = inject(ActingContextService);
  await actingContext.waitUntilLoaded();
  return actingContext.canViewStatusTracking()
    ? true
    : inject(Router).createUrlTree(['/license-application']);
};

export const routes: Routes = [
  {
    path: 'auth/action-token-resume',
    pathMatch: 'full',
    loadComponent: () =>
      import('./pages/action-token-resume/action-token-resume').then(
        (m) => m.ActionTokenResume,
      ),
  },
  {
    path: '',
    canActivateChild: [emailVerificationGate],
    loadComponent: () => import('./layout/portal-layout/portal-layout').then((m) => m.PortalLayout),
    children: [
      { path: '', pathMatch: 'full', redirectTo: 'home' },
      {
        path: 'acting-context',
        canActivate: [authRequiredGuard],
        loadComponent: () =>
          import('./pages/acting-context/acting-context').then((m) => m.ActingContext),
        data: { breadcrumb: 'ดำเนินการในนาม' },
      },
      {
        path: 'companies',
        canActivate: [authRequiredGuard, citizenOnlyGuard],
        loadComponent: () => import('./pages/company-management/company-management').then(m => m.CompanyManagement),
        data: { breadcrumb: 'จัดการองค์กร' },
      },
      {
        path: 'companies/:companyId',
        canActivate: [authRequiredGuard, citizenOnlyGuard],
        loadComponent: () => import('./pages/company-management/company-management').then(m => m.CompanyManagement),
        data: {
          breadcrumbs: [
            { label: 'จัดการองค์กร', route: '/companies' },
            { label: 'ข้อมูลและสิทธิ์ของบริษัท' },
          ],
        },
      },
      {
        path: 'email-verification',
        canActivate: [emailVerificationPageGuard],
        loadComponent: () =>
          import('./pages/email-verification/email-verification').then((m) => m.EmailVerification),
        data: {
          breadcrumb: 'ยืนยันอีเมล',
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'profile',
        canActivate: [authRequiredGuard],
        loadComponent: () => import('./pages/profile/profile').then((m) => m.Profile),
        data: { breadcrumb: 'แก้ไขข้อมูลส่วนตัว' },
      },
      {
        path: 'privacy-notice',
        title: 'ประกาศความเป็นส่วนตัว',
        loadComponent: () => import('./pages/privacy-notice/privacy-notice').then(m => m.PrivacyNotice),
        data: { breadcrumb: 'ประกาศความเป็นส่วนตัว', hideLayoutBreadcrumb: true },
      },
      {
        path: 'home',
        loadComponent: () => import('./pages/home/home').then((m) => m.Home),
        data: {
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'notifications',
        canActivate: [authRequiredGuard],
        loadComponent: () =>
          import('./pages/notifications/notifications-page').then((m) => m.NotificationsPage),
        data: {
          breadcrumb: 'การแจ้งเตือน',
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'news',
        loadComponent: () => import('./pages/news/news').then((m) => m.News),
        data: {
          breadcrumb: 'ข่าวประชาสัมพันธ์',
        },
      },
      {
        path: 'news/:id',
        loadComponent: () => import('./pages/news/news-detail').then((m) => m.NewsDetail),
        data: {
          breadcrumbs: [
            { label: 'ข่าวประชาสัมพันธ์', route: '/news' },
            { label: 'รายละเอียดข่าว' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application/license-application').then(
            (m) => m.LicenseApplication,
          ),
        data: {
          breadcrumb: 'ยื่นขอใบอนุญาต',
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/new-request',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-new/license-application-new').then(
            (m) => m.LicenseApplicationNew,
          ),
        data: {
          hideLayoutBreadcrumb: true,
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ยื่นคำขอใหม่' },
          ],
        },
      },
      {
        path: 'license-application/appeal-request',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-appeal/license-application-appeal').then(
            (m) => m.LicenseApplicationAppeal,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'อุทธรณ์คำขอ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/appeal-request/:requestId',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-appeal/license-application-appeal-request').then(
            (m) => m.LicenseApplicationAppealRequest,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'อุทธรณ์คำขอ', route: '/license-application/appeal-request' },
            { label: 'รายละเอียดคำขอ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/appeal-request/:requestId/submit',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-appeal/license-application-appeal-detail').then(
            (m) => m.LicenseApplicationAppealDetail,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'อุทธรณ์คำขอ', route: '/license-application/appeal-request' },
            { label: 'รายละเอียดคำขอ' },
            { label: 'ยื่นอุทธรณ์' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/renewal',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-drafts').then(
            (m) => m.LicenseApplicationRenewalDrafts,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ต่ออายุใบอนุญาต' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/renewal-search',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal').then(
            (m) => m.LicenseApplicationRenewal,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ต่ออายุใบอนุญาต', route: '/license-application/renewal' },
            { label: 'ค้นหาใบอนุญาตเพื่อต่ออายุ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/renewal/sign/:id',
        canActivate: [authRequiredGuard, signingAccessGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-sign').then(
            (m) => m.LicenseApplicationRenewalSign,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ต่ออายุใบอนุญาต', route: '/license-application/renewal-search' },
            { label: 'ลงนามสัญญา' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/new-request/sign/:id',
        canActivate: [authRequiredGuard, signingAccessGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-sign').then(
            (m) => m.LicenseApplicationRenewalSign,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ยื่นคำขอใหม่', route: '/license-application/new-request' },
            { label: 'ลงนามคำขอใหม่' },
          ],
          signingKind: 'NEW_REQUEST',
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/renewal/payment/:id',
        canActivate: [authRequiredGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-payment').then(
            (m) => m.LicenseApplicationRenewalPayment,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ต่ออายุใบอนุญาต', route: '/license-application/renewal-search' },
            { label: 'ชำระเงินและแนบหลักฐาน' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/new-request/payment/:id',
        canActivate: [authRequiredGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-payment').then(
            (m) => m.LicenseApplicationRenewalPayment,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ยื่นคำขอใหม่', route: '/license-application/new-request' },
            { label: 'ชำระเงินและแนบหลักฐาน' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/renewal/:licenseId',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-detail').then(
            (m) => m.LicenseApplicationRenewalDetail,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ต่ออายุใบอนุญาต', route: '/license-application/renewal' },
            { label: 'รายละเอียดใบอนุญาต' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/renewal-request/:licenseId',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-request').then(
            (m) => m.LicenseApplicationRenewalRequest,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ต่ออายุใบอนุญาต', route: '/license-application/renewal' },
            { label: 'เริ่มต่ออายุคำขอ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/contract-termination',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-contract-termination/license-contract-termination-drafts').then(
            (m) => m.LicenseContractTerminationDrafts,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'บอกเลิกสัญญา' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/contract-termination-search',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-contract-termination/license-contract-termination').then(
            (m) => m.LicenseContractTermination,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'บอกเลิกสัญญา' },
          ],
        },
      },
      {
        path: 'license-application/contract-termination/:contractId',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-contract-termination/license-contract-termination-detail').then(
            (m) => m.LicenseContractTerminationDetail,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'บอกเลิกสัญญา', route: '/license-application/contract-termination' },
            { label: 'รายละเอียดสัญญา' },
          ],
        },
      },
      {
        path: 'license-application/contract-termination-request/:contractId',
        canActivate: [authRequiredGuard, fileOnBehalfGuard],
        loadComponent: () =>
          import('./pages/license-contract-termination/license-contract-termination-request').then(
            (m) => m.LicenseContractTerminationRequest,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'บอกเลิกสัญญา', route: '/license-application/contract-termination' },
            { label: 'ยื่นคำขอบอกเลิกสัญญา' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'staff-filing-denied',
        canActivate: [authRequiredGuard],
        loadComponent: () =>
          import('./pages/staff-filing-denied/staff-filing-denied').then(
            (m) => m.StaffFilingDenied,
          ),
        data: { breadcrumb: 'ไม่มีสิทธิ์ยื่นคำขอแทนประชาชน' },
      },
      {
        path: 'license-application/status-tracking',
        canActivate: [authRequiredGuard, trackingAccessGuard],
        loadComponent: () =>
          import('./pages/license-status-tracking/license-status-tracking').then(
            (m) => m.LicenseStatusTracking,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            { label: 'ตรวจสอบ และติดตามสถานะคำขอ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/status-tracking/:requestId/payment',
        canActivate: [authRequiredGuard, trackingAccessGuard],
        loadComponent: () =>
          import('./pages/license-application-renewal/license-application-renewal-payment').then(
            (m) => m.LicenseApplicationRenewalPayment,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            {
              label: 'ตรวจสอบ และติดตามสถานะคำขอ',
              route: '/license-application/status-tracking',
            },
            { label: 'ชำระหนี้และแนบใบเสร็จ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'license-application/status-tracking/:requestId',
        canActivate: [authRequiredGuard, trackingAccessGuard],
        loadComponent: () =>
          import('./pages/license-status-tracking/license-status-tracking-detail').then(
            (m) => m.LicenseStatusTrackingDetail,
          ),
        data: {
          breadcrumbs: [
            { label: 'ยื่นขอใบอนุญาต', route: '/license-application' },
            {
              label: 'ตรวจสอบ และติดตามสถานะคำขอ',
              route: '/license-application/status-tracking',
            },
            { label: 'รายละเอียดคำขอ' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'approval',
        canActivate: [authRequiredGuard, signingAccessGuard],
        loadComponent: () =>
          import('./pages/signature-approvals-page/signature-approvals-page').then(
            (m) => m.SignatureApprovalsPage,
          ),
        data: {
          breadcrumbs: [{ label: 'ลงนามสัญญา/ลงนามพยาน' }],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'approval/:requestNo',
        canActivate: [authRequiredGuard, signingAccessGuard],
        loadComponent: () =>
          import('./pages/signature-approval-detail-page/signature-approval-detail-page').then(
            (m) => m.SignatureApprovalDetailPage,
          ),
        data: {
          breadcrumbs: [
            { label: 'ลงนามสัญญา/ลงนามพยาน', route: '/approval' },
            { label: 'รายละเอียดการลงนาม' },
          ],
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'users',
        canActivate: [userManagementGuard],
        loadComponent: () =>
          import('./pages/user-management/user-management').then((m) => m.UserManagement),
        data: {
          breadcrumb: 'จัดการผู้ใช้งาน',
          hideLayoutBreadcrumb: true,
        },
      },
      {
        path: 'users/history',
        pathMatch: 'full',
        redirectTo: 'users/history/login',
      },
      {
        path: 'users/history/login',
        canActivate: [userManagementGuard],
        loadComponent: () =>
          import('./pages/user-history/user-history').then((m) => m.UserHistory),
        data: {
          breadcrumbs: [
            { label: 'จัดการผู้ใช้งาน', route: '/users' },
            { label: 'ประวัติการใช้งาน' },
          ],
          hideLayoutBreadcrumb: true,
          historyKind: 'login',
        },
      },
      {
        path: 'users/history/exports',
        redirectTo: 'users/history/login',
      },
      {
        path: 'users/history/transactions',
        canActivate: [userManagementGuard],
        loadComponent: () =>
          import('./pages/user-history/user-history').then((m) => m.UserHistory),
        data: {
          breadcrumbs: [
            { label: 'จัดการผู้ใช้งาน', route: '/users' },
            { label: 'ประวัติการใช้งาน' },
          ],
          hideLayoutBreadcrumb: true,
          historyKind: 'transactions',
        },
      },
      {
        path: 'users/new',
        canActivate: [userManagementGuard],
        loadComponent: () =>
          import('./pages/user-management/user-access-page/user-access-page').then(
            (m) => m.UserAccessPage,
          ),
        data: { mode: 'create', hideLayoutBreadcrumb: true },
      },
      {
        path: 'users/new-governor',
        canActivate: [userManagementGuard],
        loadComponent: () =>
          import('./pages/user-management/user-access-page/user-access-page').then(
            (m) => m.UserAccessPage,
          ),
        data: { mode: 'governor', hideLayoutBreadcrumb: true },
      },
      {
        path: 'users/:id',
        canActivate: [userManagementGuard],
        loadComponent: () =>
          import('./pages/user-management/user-access-page/user-access-page').then(
            (m) => m.UserAccessPage,
          ),
        data: { mode: 'edit', hideLayoutBreadcrumb: true },
      },
    ],
  },
  { path: '**', redirectTo: '' },
];
